/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  WISHLIST MANAGER - Nutri Premium Theme                                        ║
 * ║  Sistema de Lista de Deseados con LocalStorage                                 ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */

class WishlistManager {
    constructor() {
        this.STORAGE_KEY = 'nutri_wishlist';
        this.wishlist = this.loadFromStorage();
        this.pruneOwnedProducts();
        this.init();
    }

    // Si el customer ya compró un TALLER, lo removemos automáticamente del wishlist
    // (ya tiene acceso). Ebooks/recetarios/guías comprados SE MANTIENEN: se pueden
    // recomprar (regalar, archivar) — coherente con wishlist-button.liquid que solo
    // oculta el boton para talleres ya comprados, no para ebooks/guias.
    // Los IDs vienen inyectados en window.NutriOwnedProductIds desde layout/theme.liquid.
    pruneOwnedProducts() {
        try {
            const owned = (window.NutriOwnedProductIds || []).map(String);
            if (!owned.length) return;
            const before = this.wishlist.length;
            this.wishlist = this.wishlist.filter(item => {
                const isOwned = owned.indexOf(String(item.id)) !== -1;
                const isWorkshop = (item.productType || 'ebook') === 'workshop';
                return !(isOwned && isWorkshop);
            });
            if (this.wishlist.length !== before) {
                this.saveToStorage();
            }
        } catch (e) {
            console.warn('Wishlist: pruneOwnedProducts error', e);
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // STORAGE METHODS
    // ═══════════════════════════════════════════════════════════════════════════

    loadFromStorage() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            return data ? JSON.parse(data) : [];
        } catch (e) {
            console.warn('Wishlist: Error loading from storage', e);
            return [];
        }
    }

    saveToStorage() {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.wishlist));
        } catch (e) {
            console.warn('Wishlist: Error saving to storage', e);
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // CRUD METHODS
    // ═══════════════════════════════════════════════════════════════════════════

    add(product) {
        if (!product || !product.id) return false;

        // Check if already exists
        if (this.isInWishlist(product.id)) return false;

        this.wishlist.push({
            id: String(product.id),
            handle: product.handle || '',
            title: product.title || '',
            price: product.price || 0,
            comparePrice: product.comparePrice || 0,
            image: product.image || '',
            url: product.url || '',
            productType: product.productType || 'ebook',
            label: product.label || '',
            addedAt: Date.now()
        });

        this.saveToStorage();
        this.updateAllButtons();
        this.updateHeaderCount();
        this.dispatchEvent('wishlist:added', product);
        return true;
    }

    remove(productId) {
        const id = String(productId);
        const index = this.wishlist.findIndex(item => item.id === id);

        if (index === -1) return false;

        const removed = this.wishlist.splice(index, 1)[0];
        this.saveToStorage();
        this.updateAllButtons();
        this.updateHeaderCount();
        this.dispatchEvent('wishlist:removed', removed);
        return true;
    }

    toggle(product) {
        if (this.isInWishlist(product.id)) {
            return { action: 'removed', result: this.remove(product.id) };
        } else {
            return { action: 'added', result: this.add(product) };
        }
    }

    isInWishlist(productId) {
        return this.wishlist.some(item => item.id === String(productId));
    }

    getAll() {
        return [...this.wishlist];
    }

    getCount() {
        return this.wishlist.length;
    }

    clear() {
        this.wishlist = [];
        this.saveToStorage();
        this.updateAllButtons();
        this.updateHeaderCount();
        this.dispatchEvent('wishlist:cleared');
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // UI UPDATE METHODS
    // ═══════════════════════════════════════════════════════════════════════════

    updateAllButtons() {
        document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
            const productId = btn.dataset.productId;
            const isActive = this.isInWishlist(productId);
            btn.classList.toggle('wishlist-btn--active', isActive);
            btn.setAttribute('aria-pressed', isActive);
        });
    }

    updateHeaderCount() {
        const count = this.getCount();
        document.querySelectorAll('[data-wishlist-count]').forEach(el => {
            el.textContent = count;
            el.style.display = count > 0 ? 'flex' : 'none';
        });

        // Update header icon state
        document.querySelectorAll('[data-wishlist-header-icon]').forEach(icon => {
            icon.classList.toggle('has-items', count > 0);
        });
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // EVENT HANDLING
    // ═══════════════════════════════════════════════════════════════════════════

    init() {
        // La delegación de eventos vive en `document`, así que NO depende de que el
        // DOM esté listo: se enlaza una sola vez, ahora mismo. Esto elimina la
        // ventana de "doble binding" que ocurría cuando el script se ejecutaba en
        // readyState 'interactive' (corría la rama inmediata Y la de DOMContentLoaded),
        // duplicando el listener de click y provocando un toggle net-zero (add+remove)
        // que hacía que el botón "no hiciera nada" (típico en la homepage con caché frío).
        this.bindButtons();

        // El refresco de UI (estados activos + contador) sí necesita el DOM presente.
        const refreshUI = () => {
            this.updateAllButtons();
            this.updateHeaderCount();
        };
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', refreshUI);
        } else {
            refreshUI();
        }
    }

    bindButtons() {
        // Idempotente: garantiza que los listeners delegados se registren UNA sola vez,
        // sin importar cuántas veces se invoque init()/bindButtons().
        if (this._buttonsBound) return;
        this._buttonsBound = true;

        // Use event delegation for dynamic content
        document.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-wishlist-btn]');
            if (!btn) return;

            e.preventDefault();
            e.stopPropagation();

            const product = {
                id: btn.dataset.productId,
                handle: btn.dataset.productHandle,
                title: btn.dataset.productTitle,
                price: parseInt(btn.dataset.productPrice) || 0,
                comparePrice: parseInt(btn.dataset.productComparePrice) || 0,
                image: btn.dataset.productImage,
                url: btn.dataset.productUrl,
                productType: btn.dataset.productType || 'ebook',
                label: btn.dataset.productLabel || ''
            };

            const { action } = this.toggle(product);

            // Trigger animation
            this.animateButton(btn, action);
        });

        // Remove buttons on wishlist page
        document.addEventListener('click', (e) => {
            const removeBtn = e.target.closest('[data-wishlist-remove]');
            if (!removeBtn) return;

            e.preventDefault();
            const productId = removeBtn.dataset.productId;
            const card = removeBtn.closest('[data-wishlist-card]');

            if (card) {
                card.classList.add('wishlist-card--removing');
                setTimeout(() => {
                    this.remove(productId);
                    this.dispatchEvent('wishlist:ui-refresh');
                }, 300);
            } else {
                this.remove(productId);
            }
        });
    }

    animateButton(btn, action) {
        // Remove any existing animation class
        btn.classList.remove('wishlist-btn--pulse', 'wishlist-btn--pop');

        // Force reflow
        void btn.offsetWidth;

        // Add animation class based on action
        if (action === 'added') {
            btn.classList.add('wishlist-btn--pulse');
        } else {
            btn.classList.add('wishlist-btn--pop');
        }

        // Remove animation class after completion
        setTimeout(() => {
            btn.classList.remove('wishlist-btn--pulse', 'wishlist-btn--pop');
        }, 600);
    }

    dispatchEvent(eventName, data = null) {
        window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// GLOBAL INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

window.NutriWishlist = new WishlistManager();
